import { useEffect, useState } from "react";
import { Truck, Phone, ExternalLink, Package, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { fetchYangoConfig, requestYangoQuote } from "@/lib/api";
import { fmtPrice } from "@/lib/utils";

const TRACKING_STEPS = [
  { icon: Package, label: "Colis en attente" },
  { icon: Truck, label: "Ramassage" },
  { icon: MapPin, label: "En livraison" },
  { icon: CheckCircle2, label: "Livre" },
];

export default function YangoDelivery({ annonceId, annonceCategory, annonceCity }) {
  const [config, setConfig] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [distance, setDistance] = useState(20);
  const [weight, setWeight] = useState(5);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trackingStep, setTrackingStep] = useState(0);

  useEffect(() => {
    fetchYangoConfig().then(setConfig).catch(() => {});
  }, []);

  // Recompute quote whenever inputs change & toggle is on (debounced 300ms)
  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    const t = setTimeout(() => {
      requestYangoQuote({ distance_km: distance, weight_kg: weight, delivery_city: annonceCity })
        .then(setQuote)
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [enabled, distance, weight, annonceCity]);

  const callYango = () => {
    if (!config?.phone) return;
    window.location.href = `tel:${config.phone.replace(/\s+/g, "")}`;
  };

  const reserveOnYango = () => {
    if (!config?.web_url) return;
    window.open(config.web_url, "_blank", "noopener");
  };

  const eligible = config?.eligible_categories?.includes(annonceCategory) ?? true;

  return (
    <section className="mt-6 rounded-[24px] border-2 border-orange-200 bg-gradient-to-br from-orange-50/60 to-white overflow-hidden" data-testid="yango-delivery">
      <div className="p-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 text-white grid place-items-center shadow-md">
            <Truck size={20} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">Livraison Yango</div>
            <div className="font-bold text-[15px]">Faites livrer ce produit en {config?.delivery_time || "24-48 h"}</div>
            <div className="text-xs text-[var(--ink-soft)] mt-0.5">
              Couverture : Abidjan + villes a moins de {config?.coverage_radius_km || 100} km.
              {!eligible && <span className="text-orange-600 font-semibold"> Categorie non eligible.</span>}
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer" data-testid="yango-toggle-label">
          <span className="text-sm font-semibold">{enabled ? "Active" : "Activer"}</span>
          <button
            onClick={() => setEnabled((v) => !v)}
            className={`w-12 h-7 rounded-full relative transition-colors ${enabled ? "bg-gradient-to-r from-orange-500 to-red-500" : "bg-gray-300"}`}
            data-testid="yango-toggle"
            disabled={!eligible}
            aria-label="Activer la livraison Yango"
          >
            <span className={`absolute top-0.5 ${enabled ? "left-[22px]" : "left-0.5"} w-6 h-6 bg-white rounded-full transition-all shadow`} />
          </button>
        </label>
      </div>

      {enabled && (
        <div className="border-t border-orange-200/80 bg-white/70 p-5 space-y-5" data-testid="yango-panel">
          {/* Quote */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label>Distance estimee : <b>{distance} km</b></label>
              <input
                type="range" min={1} max={120} step={1}
                value={distance}
                onChange={(e) => setDistance(Number(e.target.value))}
                data-testid="yango-distance"
              />
            </div>
            <div>
              <label>Poids estime : <b>{weight} kg</b></label>
              <input
                type="range" min={1} max={50} step={1}
                value={weight}
                onChange={(e) => setWeight(Number(e.target.value))}
                data-testid="yango-weight"
              />
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 text-white p-4 flex flex-col justify-center" data-testid="yango-quote-card">
              <div className="text-[11px] uppercase tracking-widest opacity-90">Frais estimes</div>
              <div className="text-2xl font-extrabold mt-1">
                {loading ? "..." : quote?.in_coverage ? fmtPrice(quote.fare) : "Hors zone"}
              </div>
              <div className="text-xs opacity-90 mt-1 flex items-center gap-1">
                <Clock size={12} /> {quote?.delivery_time || config?.delivery_time}
              </div>
            </div>
          </div>

          {quote && !quote.in_coverage && (
            <div className="text-sm text-orange-700 bg-orange-100 rounded-xl px-3 py-2" data-testid="yango-out-of-zone">
              {quote.reason} Contactez Yango directement pour un devis personnalise.
            </div>
          )}

          {/* Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={callYango} className="btn-primary inline-flex items-center justify-center gap-2 !bg-gradient-to-br !from-orange-500 !to-red-500" data-testid="yango-call-btn">
              <Phone size={16} /> Appeler Yango ({config?.phone || "..."})
            </button>
            <button onClick={reserveOnYango} className="btn-secondary inline-flex items-center justify-center gap-2 !border-orange-300 !text-orange-700" data-testid="yango-web-btn">
              <ExternalLink size={16} /> Reserver sur Yango
            </button>
          </div>

          {/* Tracking demo */}
          <div className="rounded-2xl bg-white border border-orange-200/70 p-4" data-testid="yango-tracking">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-sm">Suivi du colis</div>
              <div className="badge-premium" style={{ background: "linear-gradient(135deg, #fed7aa, #fb923c)", color: "#7c2d12" }}>
                #YANGO-{(annonceId || "").slice(0, 8).toUpperCase()}
              </div>
            </div>
            <div className="relative flex justify-between gap-2">
              <div className="absolute top-4 left-4 right-4 h-1 bg-gray-100 rounded">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded transition-all"
                  style={{ width: `${(trackingStep / (TRACKING_STEPS.length - 1)) * 100}%` }}
                />
              </div>
              {TRACKING_STEPS.map((s, i) => {
                const Icon = s.icon;
                const active = i <= trackingStep;
                return (
                  <div key={s.label} className="flex-1 relative z-10 text-center" data-testid={`yango-step-${i}`}>
                    <div className={`w-9 h-9 rounded-full mx-auto grid place-items-center transition-colors ${
                      active ? "bg-gradient-to-br from-orange-500 to-red-500 text-white" : "bg-gray-100 text-gray-400"
                    }`}>
                      <Icon size={15} />
                    </div>
                    <div className={`text-[11px] mt-1 font-semibold ${active ? "text-orange-700" : "text-[var(--ink-soft)]"}`}>
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-[var(--ink-soft)] text-center mt-3">
              Apercu du parcours - le statut reel sera disponible apres la prise en charge par Yango.
              <button
                onClick={() => setTrackingStep((s) => (s + 1) % TRACKING_STEPS.length)}
                className="text-orange-600 font-semibold ml-1 underline"
                data-testid="yango-step-demo"
              >
                Simuler etape suivante
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
